import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Building2,
  ChevronDown,
  Hash,
  HelpCircle,
  KeyRound,
  Loader2,
  Mail,
  Send,
  Server,
  UserRound,
  X,
} from 'lucide-react';
import './CxEmailTemplateModal.css';
import './CustomerExperienceView.css';
import './CxEmailSendMailModal.css';
import CxExpAnchorTooltip from './CxExpAnchorTooltip';

const MAIL_API = '/api/vc-usr/cx/config/mail/empresas';

function authHeaders() {
  const token = localStorage.getItem('authToken');
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
}

function draftSnapshot(row) {
  return JSON.stringify({
    email: row.email || '',
    fromEmail: row.fromEmail || '',
    senderName: row.senderName || '',
    smtpServer: row.smtpServer || '',
    smtpPort: row.smtpPort || '',
    password: row.password || '',
  });
}

function mapCompanyToDraft(company) {
  return {
    codigo: company.codigo,
    nome: company.nome,
    cnpj: company.cnpj,
    email: company.mail?.email || '',
    fromEmail: company.mail?.fromEmail || '',
    senderName: company.mail?.senderName || '',
    smtpServer: company.mail?.smtpServer || '',
    smtpPort: company.mail?.smtpPort || '',
    password: '',
    hasPassword: Boolean(company.mail?.hasPassword),
    configured: Boolean(company.mail?.configured),
    testEmail: '',
    testResult: null,
    sendResult: null,
  };
}

function CxEmailSendCompanyRow({
  row,
  baseline,
  expanded,
  onToggle,
  onChange,
  onTest,
  onSendTest,
  testing,
  sending,
  showSenderName = true,
  showFromEmail = true,
  hideHead = false,
}) {
  const canTest = row.email && row.smtpServer && row.smtpPort;
  const canSend = canTest && row.testEmail && (row.password || row.hasPassword);
  const isDirty = draftSnapshot(row) !== baseline;

  return (
    <article className={`cx-email-send-company${expanded ? ' cx-email-send-company--expanded' : ''}`}>
      {!hideHead ? (
      <div className="cx-email-send-company-head">
        <span className="cx-email-send-company-code">{row.codigo}</span>
        <div className="cx-email-send-company-meta">
          <span className="cx-email-send-company-name" title={row.nome}>{row.nome}</span>
          {row.cnpj ? <span className="cx-email-send-company-cnpj">{row.cnpj}</span> : null}
        </div>
        <span
          className={`cx-email-send-company-badge${
            row.configured ? ' cx-email-send-company-badge--ok' : ' cx-email-send-company-badge--pending'
          }`}
        >
          {row.configured ? 'Configurado' : 'Pendente'}
        </span>
        <button
          type="button"
          className="cx-email-send-company-toggle"
          onClick={() => onToggle(row.codigo)}
          aria-expanded={expanded}
          aria-label={expanded ? 'Recolher configuração de e-mail' : 'Expandir configuração de e-mail'}
        >
          <ChevronDown size={15} strokeWidth={2} aria-hidden />
        </button>
      </div>
      ) : null}
      {expanded ? (
        <div className="cx-email-send-company-body">
        <div className="cx-email-send-fields">
          <label className="cx-email-send-field">
            <span className="cx-email-send-field-label">E-mail</span>
            <span className="cx-email-send-field-input">
              <Mail size={14} strokeWidth={2} aria-hidden />
              <input
                type="email"
                value={row.email}
                onChange={(e) => onChange(row.codigo, { email: e.target.value })}
                placeholder="E-mail de autenticação SMTP"
                autoComplete="email"
              />
            </span>
          </label>
          {showFromEmail ? (
          <label className="cx-email-send-field">
            <span className="cx-email-send-field-label-row">
              <span className="cx-email-send-field-label">De</span>
              <CxExpAnchorTooltip text="Endereço From exibido ao destinatário. Em branco, usa o e-mail de autenticação." align="start" compact>
                <button
                  type="button"
                  className="cx-email-send-field-help"
                  aria-label="E-mail From (remetente)"
                >
                  <HelpCircle size={13} strokeWidth={2} aria-hidden />
                </button>
              </CxExpAnchorTooltip>
            </span>
            <span className="cx-email-send-field-input">
              <Send size={14} strokeWidth={2} aria-hidden />
              <input
                type="email"
                value={row.fromEmail || ''}
                onChange={(e) => onChange(row.codigo, { fromEmail: e.target.value })}
                placeholder={row.email || 'remetente@empresa.com'}
                autoComplete="email"
                maxLength={200}
              />
            </span>
          </label>
          ) : null}
          {showSenderName ? (
          <label className="cx-email-send-field">
            <span className="cx-email-send-field-label-row">
              <span className="cx-email-send-field-label">Nome do perfil</span>
              <CxExpAnchorTooltip text="Nome do remetente" align="start" compact>
                <button
                  type="button"
                  className="cx-email-send-field-help"
                  aria-label="Nome do remetente"
                >
                  <HelpCircle size={13} strokeWidth={2} aria-hidden />
                </button>
              </CxExpAnchorTooltip>
            </span>
            <span className="cx-email-send-field-input">
              <UserRound size={14} strokeWidth={2} aria-hidden />
              <input
                type="text"
                value={row.senderName}
                onChange={(e) => onChange(row.codigo, { senderName: e.target.value })}
                placeholder="Ex.: Atendimento ao Cliente"
                autoComplete="organization"
                maxLength={120}
              />
            </span>
          </label>
          ) : null}
          <label className="cx-email-send-field">
            <span className="cx-email-send-field-label">Senha</span>
            <span className="cx-email-send-field-input">
              <KeyRound size={14} strokeWidth={2} aria-hidden />
              <input
                type="password"
                value={row.password}
                onChange={(e) => onChange(row.codigo, { password: e.target.value })}
                placeholder={row.hasPassword ? 'Em branco mantém a atual' : 'Senha SMTP'}
                autoComplete="new-password"
              />
            </span>
          </label>
          <label className="cx-email-send-field">
            <span className="cx-email-send-field-label">Servidor SMTP</span>
            <span className="cx-email-send-field-input">
              <Server size={14} strokeWidth={2} aria-hidden />
              <input
                type="text"
                value={row.smtpServer}
                onChange={(e) => onChange(row.codigo, { smtpServer: e.target.value })}
                placeholder="Servidor SMTP"
                autoComplete="off"
              />
            </span>
          </label>
          <label className="cx-email-send-field">
            <span className="cx-email-send-field-label">Porta SMTP</span>
            <span className="cx-email-send-field-input">
              <Hash size={14} strokeWidth={2} aria-hidden />
              <input
                type="number"
                value={row.smtpPort}
                onChange={(e) => onChange(row.codigo, { smtpPort: e.target.value })}
                placeholder="587"
                autoComplete="off"
              />
            </span>
          </label>
        </div>

        <div className="cx-email-send-actions">
          <button
            type="button"
            className="cx-email-send-btn"
            onClick={() => onTest(row.codigo)}
            disabled={testing === row.codigo || !canTest}
          >
            {testing === row.codigo ? 'Testando…' : 'Testar Configurações'}
          </button>
        </div>

        {row.testResult ? (
          <div
            className={`cx-email-send-result${
              row.testResult.success ? ' cx-email-send-result--ok' : ' cx-email-send-result--err'
            }`}
            role="status"
          >
            <span>{row.testResult.message}</span>
          </div>
        ) : null}

        <div className="cx-email-send-test-row">
          <span className="cx-email-send-field-input">
            <Send size={14} strokeWidth={2} aria-hidden />
            <input
              type="email"
              value={row.testEmail}
              onChange={(e) => onChange(row.codigo, { testEmail: e.target.value })}
              placeholder="Destinatário do teste"
              autoComplete="email"
            />
          </span>
          <button
            type="button"
            className="cx-email-send-btn cx-email-send-btn--primary"
            onClick={() => onSendTest(row.codigo)}
            disabled={sending === row.codigo || !canSend}
          >
            {sending === row.codigo ? 'Enviando…' : 'Enviar teste'}
          </button>
        </div>

        {row.sendResult ? (
          <div
            className={`cx-email-send-result${
              row.sendResult.success ? ' cx-email-send-result--ok' : ' cx-email-send-result--err'
            }`}
            role="status"
          >
            <span>{row.sendResult.message}</span>
          </div>
        ) : null}

        {isDirty ? (
          <span className="cx-email-send-field-label">Alterações não salvas nesta empresa</span>
        ) : null}
        </div>
      ) : null}
    </article>
  );
}

export { CxEmailSendCompanyRow, draftSnapshot, mapCompanyToDraft };
export default function CxEmailSendMailModal({ open, onClose, onSaved, asPage = false }) {
  const [rows, setRows] = useState([]);
  const [baselines, setBaselines] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [testing, setTesting] = useState(null);
  const [sending, setSending] = useState(null);
  const [expandedCodigos, setExpandedCodigos] = useState(() => new Set());

  const loadCompanies = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(MAIL_API, { headers: authHeaders() });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || json.success === false) throw new Error(json.message || `Erro ${res.status}`);
      const list = (json.data?.companies || []).map(mapCompanyToDraft);
      const nextBaselines = {};
      for (const row of list) {
        nextBaselines[row.codigo] = draftSnapshot(row);
      }
      setRows(list);
      setBaselines(nextBaselines);
      return json.data?.summary || null;
    } catch (e) {
      setError(e.message || 'Erro ao carregar empresas');
      setRows([]);
      setBaselines({});
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (asPage || open) {
      setExpandedCodigos(new Set());
      loadCompanies();
    }
  }, [asPage, open, loadCompanies]);

  const toggleExpanded = (codigo) => {
    setExpandedCodigos((prev) => {
      const next = new Set(prev);
      if (next.has(codigo)) next.delete(codigo);
      else next.add(codigo);
      return next;
    });
  };

  const dirtyRows = useMemo(
    () => rows.filter((row) => draftSnapshot(row) !== baselines[row.codigo]),
    [rows, baselines]
  );

  const isDirty = dirtyRows.length > 0;

  const canSave = useMemo(
    () =>
      dirtyRows.length > 0
      && dirtyRows.every(
        (row) =>
          row.email
          && row.smtpServer
          && row.smtpPort
          && (row.password || row.hasPassword)
      ),
    [dirtyRows]
  );

  const patchRow = (codigo, patch) => {
    const clearsResults = ['email', 'fromEmail', 'senderName', 'password', 'smtpServer', 'smtpPort'].some((k) => k in patch);
    setRows((prev) =>
      prev.map((row) =>
        row.codigo === codigo
          ? {
              ...row,
              ...patch,
              ...(clearsResults && patch.testResult === undefined ? { testResult: null, sendResult: null } : {}),
            }
          : row
      )
    );
  };

  const payloadForRow = (row) => ({
    email: row.email,
    fromEmail: row.fromEmail || '',
    senderName: row.senderName,
    password: row.password || null,
    smtpServer: row.smtpServer,
    smtpPort: parseInt(row.smtpPort, 10) || row.smtpPort,
  });

  const handleTest = async (codigo) => {
    const row = rows.find((r) => r.codigo === codigo);
    if (!row) return;
    setTesting(codigo);
    patchRow(codigo, { testResult: null, sendResult: null });
    try {
      const res = await fetch(`${MAIL_API}/${encodeURIComponent(codigo)}/test`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(payloadForRow(row)),
      });
      const json = await res.json().catch(() => ({}));
      patchRow(codigo, {
        testResult: {
          success: res.ok && json.success !== false,
          message: json.message || (res.ok ? 'Conexão SMTP ok.' : `Erro ${res.status}`),
        },
      });
    } catch (e) {
      patchRow(codigo, { testResult: { success: false, message: e.message || 'Falha no teste' } });
    } finally {
      setTesting(null);
    }
  };

  const handleSendTest = async (codigo) => {
    const row = rows.find((r) => r.codigo === codigo);
    if (!row) return;
    setSending(codigo);
    patchRow(codigo, { sendResult: null });
    try {
      const res = await fetch(`${MAIL_API}/${encodeURIComponent(codigo)}/send-test`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ ...payloadForRow(row), testEmail: row.testEmail }),
      });
      const json = await res.json().catch(() => ({}));
      patchRow(codigo, {
        sendResult: {
          success: res.ok && json.success !== false,
          message: json.message || (res.ok ? 'E-mail enviado.' : `Erro ${res.status}`),
        },
      });
    } catch (e) {
      patchRow(codigo, { sendResult: { success: false, message: e.message || 'Falha ao enviar' } });
    } finally {
      setSending(null);
    }
  };

  const handleCancel = () => {
    if (asPage) {
      setExpandedCodigos(new Set());
      loadCompanies();
      return;
    }
    onClose?.();
  };

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    setError('');
    try {
      const res = await fetch(MAIL_API, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({
          companies: dirtyRows.map((row) => ({
            codigo: row.codigo,
            email: row.email,
            fromEmail: row.fromEmail || '',
            senderName: row.senderName,
            password: row.password || undefined,
            smtpServer: row.smtpServer,
            smtpPort: parseInt(row.smtpPort, 10) || null,
          })),
        }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || json.success === false) throw new Error(json.message || `Erro ${res.status}`);

      await loadCompanies();
      onSaved?.();
      if (!asPage) onClose?.();
    } catch (e) {
      setError(e.message || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const body = (
    <>
      <div className={asPage ? 'cx-email-send-page-body' : 'cx-email-modal-body'}>
        <div className="cx-email-send-scroll">
          <p className="cx-email-send-intro">
            Defina e-mail de autenticação, remetente (De), nome do perfil, senha, servidor e porta por empresa. Use os testes antes de salvar.
          </p>

          {error ? (
            <div className="cx-email-send-result cx-email-send-result--err" role="alert">{error}</div>
          ) : null}

          {loading ? (
            <div className="cx-email-send-loading">
              <Loader2 size={22} className="cx-exp-spin" aria-hidden />
              Carregando empresas…
            </div>
          ) : rows.length === 0 ? (
            <div className="cx-email-send-empty">
              <Building2 size={24} strokeWidth={1.5} aria-hidden />
              Nenhuma empresa ativa encontrada.
            </div>
          ) : (
            <div className="cx-email-send-list">
              {rows.map((row) => (
                <CxEmailSendCompanyRow
                  key={row.codigo}
                  row={row}
                  baseline={baselines[row.codigo]}
                  expanded={expandedCodigos.has(row.codigo)}
                  onToggle={toggleExpanded}
                  onChange={patchRow}
                  onTest={handleTest}
                  onSendTest={handleSendTest}
                  testing={testing}
                  sending={sending}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      <footer className={asPage ? 'cx-email-send-page-footer' : 'cx-email-modal-footer'}>
        <span aria-hidden="true" />
        <div className="cx-email-modal-footer-actions">
          <button
            type="button"
            className="cx-email-btn cx-email-btn--ghost cx-email-btn--link"
            onClick={handleCancel}
            disabled={saving || !isDirty}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="cx-email-btn cx-email-btn--primary"
            onClick={handleSave}
            disabled={saving || !canSave}
          >
            {saving ? 'Salvando…' : 'Salvar'}
          </button>
        </div>
      </footer>
    </>
  );

  if (asPage) {
    return (
      <div className="cx-email-send-page">
        {body}
      </div>
    );
  }

  if (!open) return null;

  return createPortal(
    <div className="modal-overlay cx-email-modal-overlay" role="presentation" onClick={onClose}>
      <div
        className="cx-email-modal cx-email-send-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cx-email-send-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="cx-email-send-header">
          <h2 id="cx-email-send-title" className="cx-email-send-title">E-mail padrão de envio</h2>
          <button type="button" className="cx-email-send-close" onClick={onClose} aria-label="Fechar">
            <X size={18} strokeWidth={2} />
          </button>
        </header>
        {body}
      </div>
    </div>,
    document.body
  );
}
